import Head from "next/head";

import NextLink from "next/link";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";
import { useSearchParams } from "next/navigation";

import isEmpty from "lodash/isEmpty";
import keys from "lodash/keys";
import values from "lodash/values";

import { Text, Heading, HTMLWrapper } from "@/storybook-components";
import Form from "./form";

import { useAppSelector } from "@/redux/store/hooks";

import VerifyAccount from "./verification";

import isRelativePath from "@/helpers/is-relative-path";
import { extractQueryParams } from "@/helpers/extract-query-params";
import { parseNextUrlFromQuery } from "@/helpers/parse-next-url";

const paramMappings = {
  type: "type",
  token: "token"
};

const App = (): JSX.Element => {
  const router = useRouter();
  const { translate } = useTranslator();
  const { name } = useAppSelector((state) => state.site.site);
  const { messageText } = useAppSelector((state) => state.messageText);
  const { isRegisteredSuccessfully } = useAppSelector(
    (state) => state.registrations
  );
  const nextPageUrl = parseNextUrlFromQuery(router.query);
  const searchParams = useSearchParams();
  const params = extractQueryParams(searchParams, paramMappings) ?? "";
  const queryParam =
    !isEmpty(params) && keys(params).length > 0 ? values(params)[0] : "";
  return (
    <>
      <Head>
        <title>{`${translate("common.register")} | ${name}`}</title>
        <meta
          name="description"
          content={translate("register.description", { name: name })}
        />
      </Head>
      {isRegisteredSuccessfully || queryParam === "email_confirm" ? (
        <VerifyAccount />
      ) : (
        <>
          <Heading as="h1" my={3} data-testid="register-participant-heading">
            {translate("common.register")}
          </Heading>
          {messageText.registrationPage && (
            <HTMLWrapper html={messageText.registrationPage as string} />
          )}
          <Form />
          <Text textAlign="center">
            {translate("register.alreadyHaveAnAccount")}&nbsp;
            <NextLink
              passHref
              data-testid="register-loginLink"
              href={
                nextPageUrl && isRelativePath(nextPageUrl)
                  ? `/login?next=${encodeURIComponent(nextPageUrl)}`
                  : "/login"
              }
            >
              {translate("common.login")}
            </NextLink>
          </Text>
        </>
      )}
    </>
  );
};

export default App;
